import React, {useState, useRef, useLayoutEffect} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link, useHistory } from 'react-router-dom';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Popover from '@mui/material/Popover';
import Paper from '@mui/material/Paper';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCartOutlined';
import { SECTION_ICONS } from '../../sectionIcons';
import LayersIcon from '@mui/icons-material/LayersOutlined';
import BookIcon from '@mui/icons-material/BookOutlined';
import MailIcon from '@mui/icons-material/MailOutlined';
import LinkIcon from '@mui/icons-material/LinkOutlined';
import PhotoIcon from '@mui/icons-material/PhotoOutlined';
import CodeIcon from '@mui/icons-material/CodeOutlined';
import WebIcon from '@mui/icons-material/WebOutlined';
import SubtitlesIcon from '@mui/icons-material/SubtitlesOutlined';
import ReceiptIcon from '@mui/icons-material/ReceiptOutlined'
import CreateIcon from '@mui/icons-material/CreateOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CompareOutlinedIcon from '@mui/icons-material/CompareOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import NavigationOutlinedIcon from '@mui/icons-material/NavigationOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import MenuBookOutlinedIcon2 from '@mui/icons-material/MenuBookOutlined';
import Divider from '@mui/material/Divider';
import { UpgradePlanHint, UpgradePlanHintArea } from '../../components/PricingPlans';
import NewBadge, { badgeSx } from '../../components/NewBadge';
import patchNotes from '../../config/patchNotes';
import PhotoSizeSelectActualOutlinedIcon from '@mui/icons-material/PhotoSizeSelectActualOutlined';
import { withTranslation, useTranslation } from 'react-i18next';
import { IntegratedAppsList } from '../app_integrations';
import EllipsisText from '../../components/EllipsisText';
import { getTranslatableResourceSections } from '../../config/translatableResourceSections';
import { SOLUTIONS_ROOT_URL } from '../../config/solutionsArticles';
import { hasBulkTranslationAccess } from '../../modules/config';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

// the submenus that only hold translatable content
const LANGUAGE_SECTIONS = ['products', 'content', 'onlinestore', 'apps', 'general'];

// a new shop without a translation language finds the sections greyed out; the hover says why
// and opens the dashboard's add-language dialog
const NoLanguageHint = ({ children }) => {
  const { t } = useTranslation();
  return <Tooltip
    placement="right"
    arrow
    title={<div style={{ padding: 4 }}>
      <Typography variant="subtitle2" component="div" style={{ marginBottom: 4 }}>{t('navigation.noLanguageHint.title')}</Typography>
      <Typography variant="body2" component="div" style={{ marginBottom: 8 }}>{t('navigation.noLanguageHint.description')}</Typography>
      {/* the tooltip's events bubble to the row's click handler, which would navigate on */}
      <Button variant="contained" size="small" component={Link} to={{ pathname: '/', state: { createLanguage: true } }} onClick={e => e.stopPropagation()}>
        {t('navigation.noLanguageHint.button')}
      </Button>
    </div>}
  >
    {children}
  </Tooltip>;
}

export const NavigationLink = (props) => {
  const { icon, primary, customStyle, disabled, selected, locked, features, neededPlan, isActive, isAfterActiveElement, size, dense, badge } = props;
  const needsLanguage = disabled && props.onboarding && !locked;

  const row = <>
      {/* Link as the component itself, not a wrapper made per render: a fresh component type
          remounts the button on every render, and the sidebar re-renders on each bulk poll */}
      <ListItemButton
        component={Link}
        to={props.to}
        disabled={disabled || locked}
        selected={selected}
        className={classNames(
          isAfterActiveElement && 'is-after-active',
          isActive && 'is-active'
        )}
        dense={dense}
      >
        <ListItemIcon className={customStyle}>{icon}</ListItemIcon>
        <ListItemText
          primary={<EllipsisText>{primary}</EllipsisText>}
          style={{
            padding: 0
          }}
        />
        {badge}
      </ListItemButton>
      {locked &&
        <UpgradePlanHint position="end" features={features} neededPlan={neededPlan} top="50%" noTooltip />
      }
    </>;

  return (
    <li style={{position: 'relative', display: 'flex'}}>
      {locked ?
        <UpgradePlanHintArea placement="right" features={features} neededPlan={neededPlan}>
          <div style={{position: 'relative', display: 'flex', flex: 1, minWidth: 0}}>{row}</div>
        </UpgradePlanHintArea>
      : needsLanguage ?
        <NoLanguageHint>
          <div style={{position: 'relative', display: 'flex', flex: 1, minWidth: 0}}>{row}</div>
        </NoLanguageHint>
      : row}
    </li>
  );
}



function ListItemLink2(props) {
  const { primary, to, target, badge } = props;
  // an absolute url has to render a plain anchor - react-router's Link would route it
  // internally and land on a nonexistent in-app path
  const isExternal = /^https?:\/\//.test(to);
  return (
    <li>
      <ListItemButton
        dense
        component={isExternal ? 'a' : Link}
        {...(isExternal
          ? { href: to, target: target || '_blank', rel: 'noopener noreferrer' }
          : { to, target })}
      >
        <ListItemText
          primary={primary}
          primaryTypographyProps={{
            style: {
              fontSize: 12
            }
          }}
        />
        {badge}
      </ListItemButton>
    </li>
  );
}
ListItemLink2.propTypes = {
  primary: PropTypes.node.isRequired,
  to: PropTypes.string.isRequired,
};

// Drawer width for the labels at hand: the widest top-level label plus the row's fixed parts -
// gutters 16 + 8, icon 32, the expand chevron 20 on the right, and 12 so the text does not touch
// the chevron. Measured on a canvas, so it does not depend on the drawer being open or
// mid-transition; written to the CSS variable the layout reads
const DRAWER_MIN = 240;
const DRAWER_MAX = 300;
const DRAWER_ROW_CHROME = 16 + 32 + 20 + 12 + 8;
const fitDrawerToLabels = () => {
  const labels = document.querySelectorAll('.side-menu .MuiListItemText-primary, .side-submenu .MuiListItemText-primary');
  if(labels.length === 0) return;
  const canvas = document.createElement('canvas').getContext('2d');
  let widest = 0;
  labels.forEach(el => {
    const cs = window.getComputedStyle(el);
    canvas.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    widest = Math.max(widest, canvas.measureText(el.textContent || '').width);
  });
  const width = Math.min(DRAWER_MAX, Math.max(DRAWER_MIN, Math.ceil(widest) + DRAWER_ROW_CHROME));
  document.documentElement.style.setProperty('--ly-drawer-width', `${width}px`);
};

export const MainListItems = (props) => {
  const { t, i18n } = useTranslation();
  const { isDrawerOpen } = props;
  // labels change with the interface language and with what the shop unlocks
  useLayoutEffect(fitDrawerToLabels, [i18n.language, props.plan, props.onboarding]);
  const history = useHistory();
  const [openMenus, setOpenMenus] = useState({});
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [activePopover, setActivePopover] = useState(null);
  const popoverTimeoutRef = useRef(null);
  const anchorTimeoutRef = useRef(null);
  let uriParts = window.location.pathname.split('/');
  const productsSections = getTranslatableResourceSections('products');


  let profileSubmenuData = [
    { to: '/profile', label: t('profile.navigation.myProfile'), icon: <AccountCircleOutlinedIcon />, isActive: uriParts[1] === 'profile' && !uriParts[2] },
    { to: '/profile/plans', label: t('profile.navigation.mySubscription'), icon: <CreditCardOutlinedIcon />, isActive: uriParts[1] === 'profile' && uriParts[2] === 'plans' },
    { to: '/profile/tickets', label: t('profile.navigation.supportTickets'), icon: <ConfirmationNumberOutlinedIcon />, locked: props.shop.shop.charge?.name !== 'premium', neededPlan: 'premium', features: [t('plans.tooltipFeatures.jumpTheLine'), t('plans.tooltipFeatures.ticketsystem')], isActive: uriParts[1] === 'profile' && uriParts[2] === 'tickets' },
  ];


  const handleToggle = (menuKey, defaultTo) => {
    if (defaultTo !== "###open###") {
      history.push(defaultTo);
    }
    
    setOpenMenus(prev => {
      // Close all other menus and toggle the current one
      const newMenus = {};
      newMenus[menuKey] = !prev[menuKey];
      return newMenus;
    });
  };

  const handlePopoverOpen = (event, menuKey) => {
    // Clear any existing timeouts
    if (popoverTimeoutRef.current) {
      clearTimeout(popoverTimeoutRef.current);
      popoverTimeoutRef.current = null;
    }
    if (anchorTimeoutRef.current) {
      clearTimeout(anchorTimeoutRef.current);
      anchorTimeoutRef.current = null;
    }
    
    // Always update the anchor and active popover when hovering
    setPopoverAnchor(event.currentTarget);
    setActivePopover(menuKey);
  };

  const handlePopoverClose = () => {
    // Set timeout to close popover
    popoverTimeoutRef.current = setTimeout(() => {
      setActivePopover(null);
    }, 150);
    
    // Set separate timeout to clear anchor (for animation)
    anchorTimeoutRef.current = setTimeout(() => {
      setPopoverAnchor(null);
    }, 350);
  };

  const handlePopoverMouseEnter = () => {
    // Cancel both timeouts when mouse enters popover
    if (popoverTimeoutRef.current) {
      clearTimeout(popoverTimeoutRef.current);
      popoverTimeoutRef.current = null;
    }
    if (anchorTimeoutRef.current) {
      clearTimeout(anchorTimeoutRef.current);
      anchorTimeoutRef.current = null;
    }
  };

  const handleStandaloneItemClick = (to) => {
    // Close all open submenus
    setOpenMenus({});
    // Navigate to the page
    history.push(to);
  };

  const renderStandaloneItem = (menuKey, to, selected, primary, icon, otherProps = {}) => {
    // clicks in the hint tooltip bubble through its portal to the row, so a locked row must not navigate
    const onRowClick = otherProps.disabled || otherProps.locked ? undefined : () => handleStandaloneItemClick(to);

    // For collapsed drawer, use popover behavior
    if (!isDrawerOpen) {
      return <React.Fragment key={menuKey}>
        <ListItem
          onClick={onRowClick}
          selected={selected}
          className={classNames(
            selected && 'is-active'
          )}
          disabled={otherProps.disabled || otherProps.locked}
          onMouseEnter={(e) => handlePopoverOpen(e, menuKey)}
          onMouseLeave={handlePopoverClose}
        >
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText primary={primary} />
        </ListItem>
        <Popover
          open={activePopover === menuKey}
          anchorEl={popoverAnchor}
          hideBackdrop
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          style={{ pointerEvents: 'none' }}
          PaperProps={{
            style: {
              pointerEvents: 'auto',
              marginLeft: 8,
              minWidth: 200
            },
            onMouseEnter: handlePopoverMouseEnter,
            onMouseLeave: handlePopoverClose
          }}
        >
          <Paper elevation={8}>
            <List component="div" disablePadding>
              <NavigationLink
                {...props}
                to={to}
                selected={selected}
                primary={primary}
                icon={icon}
                {...otherProps}
                size={'small'}
                dense
              />
            </List>
          </Paper>
        </Popover>
      </React.Fragment>
    }

    // For expanded drawer, use standard NavigationLink with click handler
    return <div key={menuKey} onClick={onRowClick}>
      <NavigationLink
        {...props}
        to={to}
        selected={selected}
        primary={primary}
        icon={icon}
        {...otherProps}
      />
    </div>
  };

  const renderSubmenu = (menuKey, icon, title, isActive, defaultTo, links, injections={ before: null, after: null, hasActiveItem: false }) => {
    // without a translation language the translatable sections have nothing to show: the
    // section itself is greyed out and says why, instead of opening onto greyed-out items
    if(props.onboarding && LANGUAGE_SECTIONS.includes(menuKey)) {
      return <NoLanguageHint key={menuKey}>
        <div style={{ display: 'flex' }}>
          <ListItemButton disabled>
            <ListItemIcon classes={{ root: 'submenu-icon' }}>{icon}</ListItemIcon>
            <ListItemText primary={title} />
          </ListItemButton>
        </div>
      </NoLanguageHint>;
    }

    // Check if any item in this submenu is active
    // an active row may live in an injected list (e.g. the integrated apps) - the connector
    // line through the static rows only draws when the submenu knows about it
    const hasActiveItem = links.some(link => link.isActive) || injections.hasActiveItem === true;

    // For collapsed drawer, use popover behavior
    if (!isDrawerOpen) {
      return <React.Fragment key={menuKey}>
        <ListItem 
          onClick={() => defaultTo !== "###open###" && history.push(defaultTo)}
          selected={isActive}
          className={classNames(
            isActive && 'is-active'
          )}
          onMouseEnter={(e) => handlePopoverOpen(e, menuKey)}
          onMouseLeave={handlePopoverClose}
        >
          <ListItemIcon classes={{ root: 'submenu-icon' }}>{icon}</ListItemIcon>
          <ListItemText primary={title} />
        </ListItem>
        <Popover
          open={activePopover === menuKey}
          anchorEl={popoverAnchor}
          hideBackdrop
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          style={{ pointerEvents: 'none' }}
          PaperProps={{
            style: {
              pointerEvents: 'auto',
              marginLeft: 8,
              minWidth: 200
            },
            onMouseEnter: handlePopoverMouseEnter,
            onMouseLeave: handlePopoverClose
          }}
        >
          <Paper elevation={8}>
            <List component="div" className={classNames('submenu', { 'has-active-item': hasActiveItem })} disablePadding>
              {injections.before &&
                injections.before()
              }
              {/* Add main menu item if it has a real URL */}
              {defaultTo !== "###open###" && (
                <NavigationLink
                  key={defaultTo}
                  {...props}
                  to={defaultTo}
                  selected={uriParts[1] === defaultTo.replace('/', '')}
                  primary={title}
                  icon={icon}
                  disabled={props.onboarding}
                  sx={{ pl: 4 }}
                  isActive={isActive}
                  size={'small'}
                  dense
                />
              )}
              {links.map(({ to, label, badge, icon: linkIcon, locked, neededPlan, features, isActive}, index ) => {
                const activeIndex = links.findIndex(l => l.isActive === true);
                const isAfterActiveElement = activeIndex !== -1 && index > activeIndex;

                return <NavigationLink
                  key={to}
                  {...props}
                  to={to}
                  selected={isActive}
                  primary={label}
                  badge={badge}
                  icon={linkIcon || icon}
                  disabled={props.onboarding && to.indexOf('/profile') === -1 && to.indexOf('/settings') === -1}
                  locked={locked}
                  neededPlan={neededPlan}
                  features={features}
                  sx={{ pl: 4 }}
                  isActive={isActive}
                  isAfterActiveElement={isAfterActiveElement}
                  size={'small'}
                  dense
                />
              })}
              {injections.after &&
                injections.after()
              }
            </List>
          </Paper>
        </Popover>
      </React.Fragment>
    }

    // For expanded drawer, use original collapse behavior
    return <React.Fragment key={menuKey}>
      <ListItem 
        onClick={() => handleToggle(menuKey, defaultTo)}
        selected={isActive}
        className={classNames(
          isActive && 'is-active'
        )}
      >
        <ListItemIcon classes={{ root: 'submenu-icon' }}>{icon}</ListItemIcon>
        <ListItemText primary={title} />
        {(openMenus[menuKey] || isActive) ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={openMenus[menuKey] || isActive} timeout="auto" unmountOnExit>
        <List component="div" className={classNames('submenu', { 'has-active-item': hasActiveItem })} disablePadding>
          {injections.before &&
            injections.before()
          }
          {links.map(({ to, label, badge, icon: linkIcon, locked, neededPlan, features, isActive}, index ) => {
            // Calculate isAfterActiveElement per item
            const activeIndex = links.findIndex(l => l.isActive === true);
            const isAfterActiveElement = activeIndex !== -1 && index > activeIndex;

            return <NavigationLink
              key={to}
              {...props}
              to={to}
              selected={isActive}
              primary={label}
              badge={badge}
              icon={null}
              disabled={props.onboarding && to.indexOf('/profile') === -1 && to.indexOf('/settings') === -1}
              locked={locked}
              neededPlan={neededPlan}
              features={features}
              sx={{ pl: 4 }}
              isActive={isActive}
              isAfterActiveElement={isAfterActiveElement}
              size={'small'}
              dense
            />
          })}
          {injections.after &&
            injections.after()
          }
        </List>
      </Collapse>
    </React.Fragment>
    
  };

  return <div>
    {renderStandaloneItem(
      'dashboard',
      '/',
      !uriParts[1],
      t('navigation.primary.dashboard'),
      <DashboardIcon />
    )}

    {/* Machine Translation */}
    {props.shop.shop.charge?.name === 'free' ?
      renderStandaloneItem(
        'machinetranslation',
        '/machinetranslation',
        uriParts[1] === 'machinetranslation',
        t('navigation.primary.machineTranslation'),
        <TranslateOutlinedIcon />,
        {
          locked: true,
          neededPlan: 'basic',
          features: [t('plans.features.auto_translations'), t('plans.features.bulk_translations'), t('plans.features.glossary_entries'), t('plans.features.available_words_log')]
        }
      )
    :
      renderSubmenu(
        'machinetranslation',
        <TranslateOutlinedIcon />,
        t('navigation.primary.machineTranslation'),
        uriParts[1] === 'machinetranslation',
        '###open###',
        [
          {
            to: '/machinetranslation/bulk',
            label: t('navigation.primary.bulkSettings'),
            icon: <TranslateOutlinedIcon />,
            locked: !hasBulkTranslationAccess(props.shop.shop.charge?.name, props.shop.shop.langify_features),
            neededPlan: 'growth',
            features: [1, 2].map(i => t(`plans.navigationHints.bulkTranslation.feature${i}`)),
            isActive: uriParts[1] === 'machinetranslation' && uriParts[2] === 'bulk'
          },
          {
            to: '/machinetranslation/wordslog',
            label: t('profile.navigation.wordsLog'),
            icon: <AssessmentOutlinedIcon />,
            locked: props.shop.shop.charge?.name === "free",
            neededPlan: 'basic',
            features: [t('plans.navigationHints.wordsLog.feature1')],
            isActive: uriParts[1] === 'machinetranslation' && uriParts[2] === 'wordslog'
          },
          {
            to: '/machinetranslation/settings',
            label: t('navigation.primary.langifySettings', 'Settings'),
            badge: <Chip label="BETA" size="small" sx={navStyles.betaBadge} />,
            icon: <ViewModuleOutlinedIcon />,
            isActive: uriParts[1] === 'machinetranslation' && uriParts[2] === 'settings'
          },
          {
            to: '/machinetranslation/glossary',
            label: t('profile.navigation.glossary'),
            badge: <Chip label="BETA" size="small" sx={navStyles.betaBadge} />,
            icon: <MenuBookOutlinedIcon2 />,
            locked: props.shop.shop.charge?.name === "free",
            neededPlan: 'basic',
            features: [1, 2].map(i => t(`plans.navigationHints.glossary.feature${i}`)),
            isActive: uriParts[1] === 'machinetranslation' && uriParts[2] === 'glossary'
          }
        ]
      )
    }

    <Divider style={{ margin: '0' }} />

    <div data-tour="nav-translate">
    {/* Products */}
    {renderSubmenu(
        'products',
        <SECTION_ICONS.products />,
        t('navigation.primary.products'),
        ['products', 'collections', ...productsSections.map(x => x.key)].includes(uriParts[1]),
        '###open###',
        [
          { to: '/products', label: t('navigation.primary.products'), icon: <SECTION_ICONS.products />, isActive: uriParts[1] === 'products' },
          { to: '/collections', label: t('navigation.primary.collections'), icon: <SECTION_ICONS.collections />, isActive: uriParts[1] === 'collections' },
          ...productsSections.map(section => ({
            to: `/${section.key}`,
            label: t(`navigation.primary.${section.key}`),
            badge: section.isNew ? <NewBadge /> : null,
            icon: section.icon,
            isActive: uriParts[1] === section.key
          }))
        ]
      )}

    {/* Content */}
    {renderSubmenu(
      'content',
      <PermMediaOutlinedIcon />,
      t('navigation.primary.content'),
      ['metaobjects', 'navigation', 'blogs', 'articles'].includes(uriParts[1]),
      '###open###',
      [
        { to: '/metaobjects', label: t('navigation.primary.metaobjects'), icon: <ViewModuleOutlinedIcon />, isActive: uriParts[1] === 'metaobjects' },
        { to: '/navigation', label: t('navigation.primary.navigation'), icon: <NavigationOutlinedIcon />, isActive: uriParts[1] === 'navigation' },
        { to: '/blogs', label: t('navigation.primary.blogs'), icon: <SECTION_ICONS.blogs />, isActive: uriParts[1] === 'blogs' },
        { to: '/articles', label: t('navigation.primary.articles'), icon: <SECTION_ICONS.articles />, isActive: uriParts[1] === 'articles' }
      ]
    )}

    {/* Online Store */}
    {renderSubmenu(
      'onlinestore',
      <LayersIcon />,
      t('navigation.primary.onlineStore'),
      ['theme', 'pages'].includes(uriParts[1]),
      '###open###',
      [
        { to: '/theme', label: t('navigation.primary.theme'), icon: <PaletteOutlinedIcon />, isActive: uriParts[1] === 'theme' },
        { to: '/pages', label: t('navigation.primary.pages'), icon: <SECTION_ICONS.pages />, isActive: uriParts[1] === 'pages' },
      ]
    )}

    {/* Apps */}
    {renderSubmenu(
      'apps',
      <CategoryOutlinedIcon />,
      t('navigation.primary.apps'),
      ['appembeds', 'appintegrations', 'apps'].includes(uriParts[1]),
      '###open###',
      [
        { to: '/appembeds', label: t('navigation.primary.appembeds'), icon: <ExtensionOutlinedIcon />, isActive: uriParts[1] === 'appembeds' },
        // on /apps/:slug the app's own row carries the active marker - claiming it here too shows two arrows
        { to: '/appintegrations', label: t('navigation.primary.integratedApps'), icon: <AppsOutlinedIcon />, isActive: uriParts[1] === 'appintegrations' }
      ],
      {
        before: null,
        after: () => <IntegratedAppsList />,
        hasActiveItem: uriParts[1] === 'apps'
      }
    )}

    {/* Settings */}
    {renderSubmenu(
      'general',
      <StoreOutlinedIcon />,
      t('navigation.primary.settings'),
      ['shop', 'shop_metafields', 'notifications'].includes(uriParts[1]),
      '###open###',
      [
        { to: '/shop', label: t('navigation.primary.shop'), icon: <StoreOutlinedIcon />, isActive: uriParts[1] === 'shop' },
        { to: '/shop_metafields', label: t('navigation.primary.shopMetafields'), icon: <LocalOfferOutlinedIcon />, isActive: uriParts[1] === 'shop_metafields' },
        { to: '/notifications', label: t('navigation.primary.notifications'), icon: <NotificationsOutlinedIcon />, isActive: uriParts[1] === 'notifications' }
      ]
    )}

    {renderStandaloneItem(
      'custom',
      '/custom',
      uriParts[1] === 'custom',
      t('navigation.primary.custom'),
      <CreateIcon />,
      {
        disabled: props.onboarding,
        locked: props.shop.shop.charge?.name === "free",
        neededPlan: 'basic',
        features: [t('plans.customContentDummy.feature1'), t('plans.customContentDummy.feature2'), t('plans.customContentDummy.feature3')]
      }
    )}

    {(typeof props.langifyFeatures.find(x => x.name === 'static_translations') === 'undefined' || props.langifyFeatures.find(x => x.name === 'static_translations') && props.langifyFeatures.find(x => x.name === 'static_translations').is_enabled) &&
      renderStandaloneItem(
        'static_theme',
        '/static_theme',
        uriParts[1] === 'static_theme',
        t('navigation.primary.static'),
        <TextFieldsIcon />,
        {
          disabled: props.onboarding,
          locked: props.shop.shop.charge?.name === "free",
          neededPlan: 'basic',
        }
      )
    }
    </div>

    <Divider style={{ margin: '0' }} />

    {/* Profile */}
    {renderSubmenu(
      'profile',
      <PersonOutlinedIcon />,
      t('profile.navigation.profile'),
      uriParts[1] === 'profile',
      '###open###',
      profileSubmenuData
    )}

    {/* Settings */}
    {renderStandaloneItem(
      'settings',
      '/settings',
      uriParts[1] === 'settings' && !uriParts[2],
      t('navigation.primary.langifySettings'),
      <SettingsOutlinedIcon />
    )}
  </div>
};


export const SecondaryListItems = ({ isDrawerOpen }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverTimeoutRef = useRef(null);
  const anchorTimeoutRef = useRef(null);

  const handleShowOnboarding = () => {
    window.localStorage.removeItem('ly_welcome_closed');
    window.dispatchEvent(new CustomEvent('ly:showOnboarding'));
    history.push('/');
  };

  const handlePopoverOpen = (event) => {
    if (popoverTimeoutRef.current) clearTimeout(popoverTimeoutRef.current);
    if (anchorTimeoutRef.current) clearTimeout(anchorTimeoutRef.current);
    setPopoverAnchor(event.currentTarget);
    setPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    popoverTimeoutRef.current = setTimeout(() => setPopoverOpen(false), 150);
    anchorTimeoutRef.current = setTimeout(() => setPopoverAnchor(null), 350);
  };

  const handlePopoverMouseEnter = () => {
    if (popoverTimeoutRef.current) clearTimeout(popoverTimeoutRef.current);
    if (anchorTimeoutRef.current) clearTimeout(anchorTimeoutRef.current);
  };

  const items = <React.Fragment>
    <li>
      <ListItemButton dense onClick={() => {window.FreshworksWidget('open', 'ticketForm');}}>
        <ListItemText
          primary={t('navigation.secondary.getSupport')}
          primaryTypographyProps={{ style: { fontSize: 12 } }}
        />
      </ListItemButton>
    </li>
    {/* the latest release's month instead of a NEW mark */}
    <ListItemLink2 to="/patchnotes" primary={t('navigation.secondary.patchNotes')} badge={<Chip label={patchNotes[0].short} size="small" sx={navStyles.betaBadge} />} />
    <li>
      <ListItemButton dense onClick={handleShowOnboarding}>
        <ListItemText
          primary={t('navigation.secondary.onboarding')}
          primaryTypographyProps={{ style: { fontSize: 12 } }}
        />
      </ListItemButton>
    </li>
    <ListItemLink2 to={SOLUTIONS_ROOT_URL} target="_blank" primary={t('navigation.secondary.documentation')} />
    <ListItemLink2 to="https://lp.langify-app.com/en/newsletter" target="_blank" primary={t('navigation.secondary.newsletter')} />
    <ListItemLink2 to="/recommended_apps" primary={t('navigation.secondary.recommendedApps')} />
    <ListItemLink2 to="/static/privacy" primary={t('navigation.secondary.privacyPolicy')} />
    <ListItemLink2 to="/static/terms" primary={t('navigation.secondary.termsOfService')} />
  </React.Fragment>;

  if (!isDrawerOpen) {
    return <React.Fragment>
      <ListItem
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        style={{ cursor: 'default' }}
      >
        <ListItemIcon><MoreHorizOutlinedIcon style={{ margin: '0 8px' }} /></ListItemIcon>
        <ListItemText primary={t('navigation.secondary.moreLinks')} />
      </ListItem>
      <Popover
        open={popoverOpen}
        anchorEl={popoverAnchor}
        hideBackdrop
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        style={{ pointerEvents: 'none' }}
        PaperProps={{
          style: { pointerEvents: 'auto', marginLeft: 8, minWidth: 200 },
          onMouseEnter: handlePopoverMouseEnter,
          onMouseLeave: handlePopoverClose
        }}
      >
        <Paper elevation={8}>
          <List component="div" disablePadding>
            {items}
          </List>
        </Paper>
      </Popover>
    </React.Fragment>;
  }

  return <div>{items}</div>;
};



const navStyles = {
  betaBadge: badgeSx,
};